import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useI18n, useLanguage, translateUserTypeLabel } from '../i18n';
import { getMessageTemplate } from '../i18n/translate';
import { useHeaderDisplayName } from '../hooks/useHeaderDisplayName';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { NotificationsPanel } from './NotificationsPanel';
import styles from './HomeHeader.module.css';
import { useUpcomingBookingNotifications } from '../hooks/useUpcomingBookingNotifications';

export function HomeHeader() {
  const { t } = useI18n();
  const { isLoggedIn, userLoginType, logout } = useAuth();
  const { openAuth, openSearch } = useAppNavigation();
  const { upcoming, hasUpcoming, loadState, loadError, refresh } = useUpcomingBookingNotifications(
    isLoggedIn,
    userLoginType,
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsPanelOpen, setNotificationsPanelOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const displayName = useHeaderDisplayName();
  const typeLabel = userLoginType ? translateUserTypeLabel(userLoginType, t) : null;

  const goSignIn = useCallback(() => {
    setMenuOpen(false);
    openAuth('login');
  }, [openAuth]);

  const goRegister = useCallback(() => {
    setMenuOpen(false);
    openAuth('register');
  }, [openAuth]);

  const onLogout = useCallback(() => {
    setMenuOpen(false);
    logout();
    openAuth('login');
  }, [logout, openAuth]);

  const onOpenSearch = useCallback(() => {
    setMenuOpen(false);
    openSearch();
  }, [openSearch]);

  const onOpenNotifications = useCallback(() => {
    setMenuOpen(false);
    setNotificationsPanelOpen(true);
  }, []);

  const onCloseNotifications = useCallback(() => {
    setNotificationsPanelOpen(false);
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [menuOpen]);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.side}>
          <button
            type="button"
            className={styles.searchButton}
            aria-label={t('header.searchTitle')}
            onClick={onOpenSearch}
          >
            <SearchIcon />
          </button>
        </div>
        <div className={styles.center}>
          {isLoggedIn ? (
            <div className={styles.userMenuWrap} ref={menuRef}>
              <button
                type="button"
                className={styles.userButton}
                aria-label={t('header.greeting', { name: displayName })}
                aria-expanded={menuOpen}
                aria-controls={menuId}
                aria-haspopup="menu"
                onClick={() => setMenuOpen((open) => !open)}
              >
                <GreetingLabel name={displayName} />
              </button>
              {menuOpen ? (
                <div id={menuId} className={styles.userMenu} role="menu">
                  {typeLabel ? (
                    <p className={styles.userMenuMeta} role="presentation">
                      {typeLabel}
                    </p>
                  ) : null}
                  <button type="button" className={styles.logoutButton} role="menuitem" onClick={onLogout}>
                    {t('header.logOut')}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <div className={styles.authActions}>
              <button type="button" className={styles.authLink} onClick={goSignIn}>
                {t('header.signIn')}
              </button>
              <span className={styles.authDivider} aria-hidden>
                ·
              </span>
              <button type="button" className={styles.authLink} onClick={goRegister}>
                {t('header.register')}
              </button>
            </div>
          )}
        </div>
        <div className={`${styles.side} ${styles.sideEnd}`}>
          <button
            type="button"
            className={styles.bellButton}
            aria-label={t('header.notificationsTitle')}
            onClick={onOpenNotifications}
          >
            <BellIcon />
            {hasUpcoming ? <span className={styles.bellDot} aria-hidden /> : null}
          </button>
        </div>
      </header>
      {notificationsPanelOpen ? (
        <NotificationsPanel
          onClose={onCloseNotifications}
          canLoadBookings={isLoggedIn}
          upcoming={upcoming}
          loadState={loadState}
          loadError={loadError}
          onRefresh={refresh}
        />
      ) : null}
    </>
  );
}

function GreetingLabel({ name }: { name: string }) {
  const { language } = useLanguage();
  const parts = getMessageTemplate(language, 'header.greeting').split('{name}');
  if (parts.length === 2) {
    return (
      <>
        {parts[0]}
        <span className={styles.greetingName}>{name}</span>
        {parts[1]}
      </>
    );
  }
  return <>{parts[0]}{name}</>;
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}
